import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createExam, fetchClasses } from "../api/client";
import type { ClassSummary } from "../api/types";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

type FormState = {
  session_name: string;
  test_date: string;
  description: string;
  class_id: string;
  max_points: string;
};

const today = new Date().toISOString().slice(0, 10);

const CreateSessionPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("pages");
  const [form, setForm] = useState<FormState>({
    session_name: "",
    test_date: today,
    description: "",
    class_id: "",
    max_points: "",
  });
  const [classes, setClasses] = useState<ClassSummary[]>([]);
  const [status, setStatus] = useState<"idle" | "submitting" | "error" | "success">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClasses()
      .then((rows) => {
        setClasses(rows);
        if (rows.length > 0) {
          setForm((prev) => ({
            ...prev,
            class_id: prev.class_id || String(rows[0].id),
          }));
        }
      })
      .catch(() => {
        setClasses([]);
      });
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setError(null);
    try {
      const fd = new FormData();
      fd.append("session_name", form.session_name);
      fd.append("test_date", form.test_date);
      fd.append("description", form.description);
      if (form.class_id) fd.append("class_id", form.class_id);
      fd.append("max_points", form.max_points);

      const { exam_id } = await createExam(fd);
      setStatus("success");
      navigate(`/exams/${exam_id}/setup`);
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : t("createSession.createError"));
    }
  };

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-16 mb-24">
        <button className="btn btn-ghost" onClick={() => navigate("/exams")}>
          <ArrowLeft size={16} />
          {t("common.back")}
        </button>
        <div>
          <h2 className="section-title">{t("createSession.title")}</h2>
          <p className="text-sm text-muted mt-4">
            {t("createSession.subtitle")}
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} style={{ maxWidth: 720 }}>
        {/* Exam details card */}
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-20">{t("createSession.cardTitle")}</h3>

          <div className="flex flex-col gap-16">
            <div className="form-group">
              <label className="form-label">{t("createSession.nameLabel")}</label>
              <input
                type="text"
                className="form-input"
                placeholder={t("createSession.namePlaceholder")}
                required
                value={form.session_name}
                onChange={(e) => update("session_name", e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t("createSession.dateLabel")}</label>
                <input
                  type="date"
                  className="form-input"
                  required
                  value={form.test_date}
                  onChange={(e) => update("test_date", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t("createSession.maxPointsLabel")}</label>
                <input
                  type="number"
                  className="form-input"
                  min={1}
                  required
                  placeholder={t("createSession.maxPointsPlaceholder")}
                  value={form.max_points}
                  onChange={(e) => update("max_points", e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t("createSession.classLabel")}</label>
                <select
                  className="form-select"
                  required
                  value={form.class_id}
                  onChange={(e) => update("class_id", e.target.value)}
                >
                  {classes.length === 0 && (
                    <option value="">{t("createSession.classNotFound")}</option>
                  )}
                  {classes.map((cls) => (
                    <option key={cls.id} value={String(cls.id)}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t("createSession.descriptionLabel")}</label>
              <textarea
                className="form-input"
                rows={3}
                placeholder={t("createSession.descriptionPlaceholder")}
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div
            className="mb-24"
            style={{
              padding: "12px 16px",
              borderRadius: "var(--radius-md)",
              background: "var(--color-danger-bg)",
              color: "var(--color-danger)",
              fontSize: "var(--text-sm)",
            }}
          >
            {error}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-12">
          <button
            className="btn btn-primary"
            disabled={status === "submitting"}
            type="submit"
          >
            {status === "submitting" ? t("createSession.submitCreating") : t("createSession.submit")}
          </button>
          <button
            className="btn btn-outline"
            type="button"
            onClick={() => navigate("/exams")}
          >
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateSessionPage;
